import React from "react";
import { useNavigate } from "react-router-dom";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
    Users,
    UserCheck,
    UserMinus,
    AlertCircle,
    Clock,
    History,
    Calendar,
    Plus,
    FileText,
    Settings
} from "lucide-react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/ui/table";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";
import { Calendar as CalendarUI } from "../../components/ui/calendar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { api } from "../../utils/api";
import { ChevronDown, MoreVertical } from "lucide-react";

const AttendanceDashboard = () => {
    const navigate = useNavigate();
    const [date, setDate] = React.useState<Date | undefined>(new Date());

    const [statsData, setStatsData] = React.useState({
        totalEmployees: 0,
        presentToday: 0,
        onLeaveToday: 0,
        sickLeaveToday: 0,
        pendingRequests: 0,
        absentToday: 0
    });
    const [overviewData, setOverviewData] = React.useState<{
        employees: any[];
        totalCount: number;
        totalPages: number;
        currentPage: number;
    }>({
        employees: [],
        totalCount: 0,
        totalPages: 0,
        currentPage: 1
    });
    const [searchTerm, setSearchTerm] = React.useState("");
    const [currentPage, setCurrentPage] = React.useState(1);
    const [statusFilter, setStatusFilter] = React.useState<'all' | 'present' | 'onLeave'>('all');
    const [loading, setLoading] = React.useState(true);
    const [overviewLoading, setOverviewLoading] = React.useState(true);
    const [calendarStats, setCalendarStats] = React.useState({
        onLeaveOnDate: 0,
        upcomingRequests: 0
    });
    const [calendarLoading, setCalendarLoading] = React.useState(true);

    const fetchDashboardStats = async () => {
        try {
            const response = await api.get("/api/attendance/dashboard-stats");
            if (response.success) {
                setStatsData(response.data);
            }
        } catch (error) {
            console.error("Error fetching dashboard stats:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchTodayOverview = async (page: number, search: string, filter: 'all' | 'present' | 'onLeave') => {
        setOverviewLoading(true);
        try {
            const response = await api.get(`/api/attendance/today-overview?page=${page}&limit=10&search=${search}&filter=${filter}`);
            if (response.success) {
                setOverviewData(response.data);
            }
        } catch (error) {
            console.error("Error fetching today overview:", error);
        } finally {
            setOverviewLoading(false);
        }
    };

    const fetchCalendarStats = async (selectedDate: Date) => {
        setCalendarLoading(true);
        try {
            // Use local date for the query to ensure it matches what the user sees
            const year = selectedDate.getFullYear();
            const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
            const day = String(selectedDate.getDate()).padStart(2, '0');
            const dateStr = `${year}-${month}-${day}`;

            const response = await api.get(`/api/attendance/calendar-stats?date=${dateStr}`);
            if (response.success) {
                setCalendarStats(response.data);
            }
        } catch (error) {
            console.error("Error fetching calendar stats:", error);
        } finally {
            setCalendarLoading(false);
        }
    };

    // Refetch when window gains focus (user returns to tab)
    React.useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                fetchDashboardStats();
                fetchTodayOverview(currentPage, searchTerm, statusFilter);
                if (date) {
                    fetchCalendarStats(date);
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [currentPage, searchTerm, date, statusFilter]);

    // Polling: refresh stats every 5 minutes
    React.useEffect(() => {
        const interval = setInterval(() => {
            fetchDashboardStats();
            fetchTodayOverview(currentPage, searchTerm, statusFilter);
            if (date) {
                fetchCalendarStats(date);
            }
        }, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [currentPage, searchTerm, date, statusFilter]);

    // Daily boundary check: if the date has changed, refresh everything
    const lastCheckedDateRef = React.useRef<string>(new Date().toDateString());
    React.useEffect(() => {
        const checkDateInterval = setInterval(() => {
            const currentDateStr = new Date().toDateString();
            if (currentDateStr !== lastCheckedDateRef.current) {
                lastCheckedDateRef.current = currentDateStr;
                fetchDashboardStats();
                fetchTodayOverview(currentPage, searchTerm, statusFilter);
                if (date) {
                    fetchCalendarStats(date);
                }
            }
        }, 30000); // Check every 30 seconds
        return () => clearInterval(checkDateInterval);
    }, [currentPage, searchTerm, date, statusFilter]);

    React.useEffect(() => {
        fetchDashboardStats();
    }, []);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            fetchTodayOverview(currentPage, searchTerm, statusFilter);
        }, 300); // Debounce search
        return () => clearTimeout(timer);
    }, [currentPage, searchTerm, statusFilter]);

    React.useEffect(() => {
        if (date) {
            fetchCalendarStats(date);
        }
    }, [date]);

    const stats = [
        {
            title: "Total Employees",
            value: loading ? "..." : statsData.totalEmployees.toString(),
            icon: <Users className="h-5 w-5 text-blue-600" />,
            color: "text-blue-600",
            bgColor: "bg-blue-50",
            caption: "Staff with leave entitlements",
            filter: 'all' as const
        },
        {
            title: "Present Today",
            value: loading ? "..." : statsData.presentToday.toString(),
            icon: <UserCheck className="h-5 w-5 text-green-600" />,
            color: "text-green-600",
            bgColor: "bg-green-50",
            filter: 'present' as const
        },
        {
            title: "On Leave Today",
            value: loading ? "..." : statsData.onLeaveToday.toString(),
            icon: <UserMinus className="h-5 w-5 text-orange-600" />,
            color: "text-orange-600",
            bgColor: "bg-orange-50",
            filter: 'onLeave' as const
        },

        {
            title: "Pending Requests",
            value: loading ? "..." : statsData.pendingRequests.toString(),
            icon: <Clock className="h-5 w-5 text-purple-600" />,
            color: "text-purple-600",
            bgColor: "bg-purple-50",
            path: "/attendance/approvals"
        }
    ];

    const formattedDate = new Intl.DateTimeFormat('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    }).format(new Date());

    const ctaActions = [
        {
            title: "Request Leave",
            subtitle: "Submit a new leave request",
            icon: <Plus className="h-5 w-5 text-blue-600" />,
            bgColor: "bg-blue-50",
            onClick: () => navigate('/attendance/request-leave')
        },
        {
            title: "Manage Leave Requests",
            subtitle: "Review team leave requests",
            icon: <FileText className="h-5 w-5 text-purple-600" />,
            bgColor: "bg-purple-50",
            onClick: () => navigate('/attendance/approvals')
        },
        {
            title: "Manager Report Absence",
            subtitle: "Keep the team informed someone is away today",
            icon: <UserMinus className="h-5 w-5 text-red-600" />,
            bgColor: "bg-red-50",
            onClick: () => navigate('/attendance/report-absence')
        },
        {
            title: "Administration",
            subtitle: "Manage balances and settings",
            icon: <Users className="h-5 w-5 text-green-600" />,
            bgColor: "bg-green-50",
            onClick: () => navigate('/attendance/admin')
        }
    ];

    return (
        <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Attendance & Leave Management</h1>
                    <p className="text-gray-500 mt-1">
                        Track attendance, manage leave requests, and view team availability
                    </p>
                </div>
                <div className="hidden md:flex items-center gap-3">
                    <Button
                        variant="outline"
                        className="flex items-center gap-2"
                        onClick={() => navigate('/attendance/settings')}
                    >
                        <Settings className="h-4 w-4" />
                        Settings
                    </Button>
                    <Button
                        variant="outline"
                        className="flex items-center gap-2"
                        onClick={() => navigate('/attendance/history')}
                    >
                        <History className="h-4 w-4" />
                        View History
                    </Button>
                    <Button
                        variant="outline"
                        className="flex items-center gap-2"
                        onClick={() => navigate('/attendance/calendar')}
                    >
                        <Calendar className="h-4 w-4" />
                        View Calendar
                    </Button>
                </div>

                {/* Mobile Header Dropdown */}
                <div className="md:hidden">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="flex items-center gap-2 w-full justify-center">
                                <MoreVertical size={18} />
                                <span>Actions</span>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56 rounded-xl border-gray-100 shadow-lg">
                            <DropdownMenuItem onClick={() => navigate('/attendance/history')} className="py-3 cursor-pointer rounded-lg mx-1">
                                <History className="h-4 w-4 mr-2" />
                                <span className="font-bold text-sm">View History</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate('/attendance/calendar')} className="py-3 cursor-pointer rounded-lg mx-1">
                                <Calendar className="h-4 w-4 mr-2" />
                                <span className="font-bold text-sm">View Calendar</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate('/attendance/settings')} className="py-3 cursor-pointer rounded-lg mx-1">
                                <Settings className="h-4 w-4 mr-2" />
                                <span className="font-bold text-sm">Settings</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {stats.map((stat: any, index: number) => (
                    <Card
                        key={index}
                        className={`border shadow-sm hover:shadow-md transition-all duration-200 ${
                            stat.path || stat.filter ? 'cursor-pointer active:scale-95' : ''
                        } ${
                            stat.filter && statusFilter === stat.filter
                                ? 'ring-2 ring-blue-400 border-blue-400'
                                : 'border-gray-100 hover:border-blue-200'
                        }`}
                        onClick={() => {
                            if (stat.path) {
                                navigate(stat.path);
                            } else if (stat.filter) {
                                setStatusFilter(stat.filter);
                                setCurrentPage(1);
                            }
                        }}
                    >
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className={`text-sm font-medium ${stat.filter && statusFilter === stat.filter ? 'text-blue-700' : 'text-gray-500'}`}>
                                {stat.title}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                                    {stat.icon}
                                </div>
                                <div>
                                    <div className="text-2xl font-bold">{stat.value}</div>
                                    {stat.caption && (
                                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">
                                            {stat.caption}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {ctaActions.map((action, index) => (
                    <button
                        key={index}
                        onClick={action.onClick}
                        className="flex items-center p-4 border border-gray-100 shadow-sm rounded-xl hover:shadow-md transition-shadow bg-white text-left w-full"
                    >
                        <div className={`p-2.5 rounded-lg flex-shrink-0 mr-3 ${action.bgColor}`}>
                            {action.icon}
                        </div>
                        <div>
                            <div className="font-semibold text-sm text-gray-900">{action.title}</div>
                            <div className="text-gray-500 text-xs mt-0.5">{action.subtitle}</div>
                        </div>
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Today's Overview Table */}
                <div className="lg:col-span-2">
                    <Card className="border border-gray-100 shadow-sm h-full">
                        <CardHeader className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <div className="flex items-center gap-2">
                                    <CardTitle className="text-xl font-bold text-gray-900">Today's Overview</CardTitle>
                                    {statusFilter === 'present' && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            Present Today
                                        </span>
                                    )}
                                    {statusFilter === 'onLeave' && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                            On Leave Today
                                        </span>
                                    )}
                                </div>
                                <CardDescription className="text-gray-500">
                                    Employee attendance status for {formattedDate}
                                </CardDescription>
                            </div>
                            <div className="relative w-full md:w-64">
                                <input
                                    type="text"
                                    placeholder="Search employees..."
                                    className="w-full pl-3 pr-10 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>
                        </CardHeader>
                        <CardContent>
                            <Table>
                                <TableHeader>
                                    <TableRow className="hover:bg-transparent border-b border-gray-100">
                                        <TableHead className="font-semibold text-gray-700">Employee</TableHead>
                                        <TableHead className="font-semibold text-gray-700">Department</TableHead>
                                        <TableHead className="font-semibold text-gray-700">Manager</TableHead>
                                        <TableHead className="font-semibold text-gray-700">Status</TableHead>
                                        <TableHead className="font-semibold text-gray-700">Duration</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {overviewLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                                                Loading employees...
                                            </TableCell>
                                        </TableRow>
                                    ) : overviewData.employees.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                                                No employees found matching your search.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        overviewData.employees.map((row, index) => (
                                            <TableRow key={index} className="hover:bg-gray-50/50 border-b border-gray-50 last:border-0 transition-colors">
                                                <TableCell className="py-4">
                                                    <div className="flex items-center gap-3">
                                                        <Avatar className="h-8 w-8 text-[10px] font-medium border border-gray-100 shadow-sm">
                                                            <AvatarFallback className="bg-gray-50 text-gray-600">
                                                                {row.initials}
                                                            </AvatarFallback>
                                                        </Avatar>
                                                        <span className="font-medium text-gray-900">{row.name}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-gray-600">{row.department}</TableCell>
                                                <TableCell className="text-gray-600">{row.manager}</TableCell>
                                                <TableCell>
                                                    <Badge
                                                        variant="outline"
                                                        className={`font-medium rounded-full px-3 py-0.5 ${row.status === "Present"
                                                            ? "bg-green-50 text-green-700 border-green-200"
                                                            : row.status === "Annual Leave"
                                                                ? "bg-green-50 text-green-700 border-green-200"
                                                                : row.status === "Personal Leave"
                                                                    ? "bg-blue-50 text-blue-700 border-blue-200"
                                                                    : row.status === "Unpaid Leave"
                                                                        ? "bg-orange-50 text-orange-700 border-orange-200"
                                                                        : row.status === "Emergency Leave"
                                                                            ? "bg-red-50 text-red-700 border-red-200"
                                                                            : "bg-gray-50 text-gray-700 border-gray-200"
                                                            }`}
                                                    >
                                                        {row.status}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-gray-500">{row.duration}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>

                            {/* Pagination Controls */}
                            {overviewData.totalPages > 1 && (
                                <div className="flex items-center justify-between mt-6 px-2">
                                    <div className="text-sm text-gray-500">
                                        Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, overviewData.totalCount)} of {overviewData.totalCount} employees
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentPage === 1 || overviewLoading}
                                        >
                                            Previous
                                        </Button>
                                        <div className="text-sm font-medium px-2">
                                            Page {currentPage} of {overviewData.totalPages}
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCurrentPage(prev => Math.min(overviewData.totalPages, prev + 1))}
                                            disabled={currentPage === overviewData.totalPages || overviewLoading}
                                        >
                                            Next
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Leave Calendar Section */}
                <div className="lg:col-span-1">
                    <Card className="border border-gray-100 shadow-sm h-full">
                        <CardHeader>
                            <CardTitle className="text-xl font-bold text-gray-900">Leave Calendar</CardTitle>
                            <CardDescription className="text-gray-500">
                                View leave schedule
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="border border-gray-100 rounded-xl p-2 bg-white flex justify-center shadow-sm">
                                <CalendarUI
                                    mode="single"
                                    selected={date}
                                    onSelect={setDate}
                                    className="rounded-md"
                                    captionLayout="dropdown"
                                    fromYear={2000}
                                    toYear={2050}
                                />
                            </div>

                            <div className="space-y-3">
                                <div className="flex justify-between items-center py-2 border-b border-gray-50">
                                    <span className="text-gray-600">
                                        Employees on leave {date?.toDateString() === new Date().toDateString() ? "today" : ""}
                                    </span>
                                    <span className="font-bold text-gray-900">{calendarLoading ? "..." : calendarStats.onLeaveOnDate}</span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-gray-50">
                                    <span className="text-gray-600">Upcoming leave requests</span>
                                    <span className="font-bold text-gray-900">{calendarLoading ? "..." : calendarStats.upcomingRequests}</span>
                                </div>
                            </div>

                            <Button
                                variant="outline"
                                className="w-full mt-4 border-gray-200 text-gray-700 hover:bg-gray-50"
                                onClick={() => navigate('/attendance/calendar')}
                            >
                                View Full Calendar
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default AttendanceDashboard;
